import { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, File, CheckCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const VoiceTraining = ({ onComplete }) => {
  const [samples, setSamples] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const loadSamples = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/voice/samples`);
      setSamples(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error('Error loading samples:', error);
    }
  }, []);

  useEffect(() => {
    loadSamples();
  }, [loadSamples]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.txt')) {
      toast.error('Please upload a .txt file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/voice/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Writing sample uploaded! AI will learn from your voice.');
      loadSamples();
      if (onComplete) onComplete();
    } catch (error) {
      toast.error('Failed to upload sample');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className='p-6 border border-border bg-surface' data-testid='voice-training-panel'>
      <h3 className='text-xl font-semibold mb-4'>Train AI in Your Voice</h3>
      <p className='text-sm text-muted-foreground mb-4'>
        Upload samples of your spiritual writing so the AI can learn your unique voice, tone, and style.
      </p>

      <div className='space-y-3'>
        <input
          ref={fileInputRef}
          type='file'
          accept='.txt'
          onChange={handleFileUpload}
          className='hidden'
          data-testid='voice-upload-input'
        />
        
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className='w-full bg-white text-black hover:bg-gray-200'
          data-testid='voice-upload-button'
        >
          <Upload size={20} className='mr-2' />
          {uploading ? 'Uploading...' : 'Upload Writing Sample (.txt)'}
        </Button>

        {samples.length > 0 && (
          <div className='mt-4'>
            <p className='text-sm font-medium mb-2'>Uploaded Samples:</p>
            {samples.map((sample) => (
              <div
                key={sample.id}
                className='flex items-center gap-2 p-2 text-sm text-muted-foreground'
                data-testid={`sample-${sample.id}`}
              >
                <CheckCircle size={16} className='text-green-500' />
                <File size={16} />
                <span>{sample.filename}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
